import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, ApiError, getDevUser, setDevUser } from './client';
import type { Me } from './types';

/* ---------------------------------------------------------------------------
   The signed-in person and what they may do, for every Cyntox site. Each app
   wraps its own state inside this: nothing app-specific loads until we know
   who someone is, and someone with no role never gets as far as the app.
   --------------------------------------------------------------------------- */

export interface PlatformState {
  me: Me;
  can: (resource: string, permission: string) => boolean;
  /** Where a capability applies: null = every location, [] = not granted. */
  locationsFor: (resource: string, permission: string) => string[] | null;
  /** Dev-mode role switcher. There is no switcher in production. */
  devUser: string | null;
  switchDevUser: (upn: string) => void;
}

const Ctx = createContext<PlatformState | null>(null);

export function usePlatform(): PlatformState {
  const v = useContext(Ctx);
  if (!v) throw new Error('usePlatform must be used inside <PlatformProvider>');
  return v;
}

/**
 * @param appLabel how messages name this app mid-sentence, e.g. "the scorecard".
 */
export function PlatformProvider({ appLabel, children }: { appLabel: string; children: ReactNode }) {
  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.get<Me>('/api/me'), retry: false });
  const [devUser, setDevUserState] = useState<string | null>(getDevUser());

  const value = useMemo<PlatformState | null>(() => {
    const me = meQuery.data;
    if (!me) return null;
    return {
      me,
      can: (resource, permission) => me.capabilities.includes(`${resource}:${permission}`),
      locationsFor: (resource, permission) => {
        const codes = me.scopes[`${resource}:${permission}`];
        return codes === undefined ? [] : codes;
      },
      devUser,
      switchDevUser: (upn: string) => {
        setDevUser(upn);
        setDevUserState(upn);
        // A different person means a different capability set and a different
        // location scope, so start clean rather than reconciling caches, and
        // start where that person starts. Reloading the current page instead
        // left the new person looking at whatever the last one had open.
        window.location.assign('/');
      },
    };
  }, [meQuery.data, devUser]);

  if (meQuery.isError) {
    const err = meQuery.error as Error;
    // A 403 is the API answering and saying no: this person has no access
    // yet. "Cannot reach the API" would send them looking for the wrong fix.
    if (err instanceof ApiError && err.status === 403) {
      return <NotSetUp message={err.message} devUser={devUser} />;
    }
    return (
      <Bootstrap title="Cannot reach the API">
        <p>
          {capitalise(appLabel)} could not load your account. Check that its API is running and that the database
          is reachable.
        </p>
        <p style={{ color: 'var(--muted)', fontSize: 12 }}>{String(err.message)}</p>
      </Bootstrap>
    );
  }

  if (!value) return <Bootstrap title={`Loading ${appLabel}…`}>{null}</Bootstrap>;

  if (value.me.capabilities.length === 0) {
    return (
      <NotSetUp
        message={`You're signed in as ${value.me.upn}, but no role has been assigned to you yet.`}
        devUser={devUser}
      />
    );
  }

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * A screen's access check, at the route. The menu hides what someone cannot
 * use, but every URL is still typeable, and a screen whose API calls are
 * refused would otherwise render as an empty list — telling someone there is
 * no data when the truth is they lack access. Any one of `any` is enough.
 */
export function Gate({ any, children }: { any: [resource: string, permission: string][]; children: ReactNode }) {
  const { can } = usePlatform();
  if (any.some(([resource, permission]) => can(resource, permission))) return <>{children}</>;
  return (
    <div className="card" style={{ maxWidth: 520 }}>
      <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>You don't have access to this screen</div>
      <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-secondary)', margin: 0 }}>
        Your role doesn't include it. Ask an administrator if you need it, or{' '}
        <Link to="/">go to your start page</Link>.
      </p>
    </div>
  );
}

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function NotSetUp({ message, devUser }: { message: string; devUser: string | null }) {
  return (
    <Bootstrap title="Your account isn't set up yet">
      <p>{message}</p>
      <p style={{ color: 'var(--muted)', fontSize: 12 }}>
        An administrator adds people and assigns their roles on the Administration screen in Admin.
      </p>
      {/* Development only: a remembered "View as" user can go stale. */}
      {devUser && (
        <button className="btn btn--sm" onClick={() => { setDevUser(null); window.location.assign('/'); }}>
          Stop viewing as {devUser}
        </button>
      )}
    </Bootstrap>
  );
}

/** A full-page card for the moments before an app can render: loading, no access, no API. */
export function Bootstrap({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: 40 }}>
      <div className="card" style={{ maxWidth: 520 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--blue)', marginBottom: 10 }}>
          CYNTOX BIOHAZARD SOLUTIONS
        </div>
        <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>{title}</div>
        <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-secondary)' }}>{children}</div>
      </div>
    </div>
  );
}
